// @flow
import React from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import makeStyles from '@material-ui/core/styles/makeStyles';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import type {Order} from '../../../../entities';
import type {OrderRequest} from '../../api';
import {colors} from '../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {commaFormat} from '../../../../utils/number';
import {useCallback} from 'react';
import {useStoreCurrency} from '../../../currency/utils';

type Props = {
  isSaving?: boolean,
  open: boolean,
  handleClose: () => void,
  updateOrderStateWithErrorCheck: OrderRequest => void,
  order: Order,
  isFetchingOrderStockErrors?: boolean,
};
const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 340,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  nOrder: {
    color: colors.grey900,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  date: {
    color: colors.grey600,
    fontWeight: 400,
  },
  priceBox: {
    backgroundColor: colors.grey200,
    borderRadius: '8px',
    width: '100%',
    margin: theme.spacing(2, 0),
    padding: theme.spacing(1, 2),
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: theme.spacing(1),
  },
  text: {
    color: colors.grey800,
  },
  total: {
    color: colors.grey900,
    fontWeight: 600,
  },
  textField: {
    width: '100%',
  },
  textInput: {
    borderRadius: '8px',
  },
  textInputLabel: {
    color: colors.grey600,
  },
  tip: {
    margin: theme.spacing(2, 0),
    color: colors.grey700,
    fontSize: '0.75rem',
  },
  button: {
    height: 40,
    fontSize: '1rem',
    alignSelf: 'stretch',
    marginBottom: theme.spacing(1),
  },
  labelDscto: {
    color: colors.success,
    fontSize: '0.875rem',
    margin: '2px 0px',
  },
  chipDscto: {
    background: '#E04763',
    fontSize: '0.875rem',
    borderRadius: 24,
    color: 'white',
    padding: '0 8px',
    marginRight: 8,
  },
}));

export default function PendingModal(props: Props) {
  const {
    isSaving,
    open,
    handleClose,
    order,
    updateOrderStateWithErrorCheck,
    isFetchingOrderStockErrors,
  } = props;
  const classes = useStyles();

  const currency = useStoreCurrency();

  const {register, handleSubmit, errors} = useForm();

  const dateString =
    order &&
    new Intl.DateTimeFormat('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(order.createdAt);

  const handleCloseModal = useCallback(() => {
    if (!isSaving) {
      handleClose();
    }
  }, [handleClose, isSaving]);

  const handleAmountReceivedSubmit = data => {
    if (data.amountReceived) {
      updateOrderStateWithErrorCheck({
        id: order.id,
        amountReceived: data.amountReceived,
        state: 'PAID',
        previousState: order.orderState,
        orderCreationType: order.orderCreationType,
      });
    }
  };

  const disableInputs = isSaving || isFetchingOrderStockErrors;

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography className={classes.title} align="center">
          Cambiar a Pagado
        </Typography>
        <Divider className={classes.divider} />
        <Typography
          className={classes.nOrder}
        >{`Nº Orden ${order.id}`}</Typography>
        <Typography
          className={classes.date}
        >{`Fecha: ${dateString}`}</Typography>
        <div className={classes.priceBox}>
          <div className={classes.priceRow}>
            <Typography className={classes.text}>Subtotal</Typography>
            <Typography className={classes.text}>
              {commaFormat(order.invoice?.detail?.products, currency?.symbol)}
            </Typography>
          </div>
          {order.invoice.detail.discount &&
            order.invoice.detail.discount.total_discount && (
              <div className={classes.priceRow}>
                <Typography className={classes.labelDscto}>
                  Descuento
                </Typography>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Typography className={classes.labelDscto}>
                    {`-${commaFormat(
                      Number(
                        order.invoice.detail.discount.total_discount
                          ? order.invoice.detail.discount.total_discount
                          : 0
                      ) * -1,
                      currency?.symbol
                    )}`}
                  </Typography>
                </div>
              </div>
            )}
          <div className={classes.priceRow}>
            <Typography className={classes.text}>Envío</Typography>
            <Typography className={classes.text}>
              {commaFormat(
                order.invoice.detail.last_mile_shipping || '--',
                currency?.symbol
              )}
            </Typography>
          </div>
          <div className={classes.priceRow}>
            <Typography className={`${classes.text} ${classes.total}`}>
              Total
            </Typography>
            <Typography className={`${classes.text} ${classes.total}`}>
              {commaFormat(order.invoice.detail.total, currency?.symbol)}
            </Typography>
          </div>
        </div>
        <TextField
          variant="outlined"
          className={classes.textField}
          label="Monto Recibido"
          InputProps={{
            className: classes.textInput,
            startAdornment: (
              <InputAdornment position="start">
                {currency?.symbol}
              </InputAdornment>
            ),
          }}
          InputLabelProps={{className: classes.textInputLabel}}
          name="amountReceived"
          defaultValue={order.invoice.detail.total}
          error={!!errors.amountReceived}
          helperText={
            errors.amountReceived ? errors.amountReceived.message : ''
          }
          inputRef={register({
            required: 'Campo obligatorio',
            max: {
              value: Number(order.invoice.detail.total),
              message:
                'El monto recibido debe ser igual o menor al monto total',
            },
          })}
          type="number"
          inputProps={{step: '.01'}}
          disabled={disableInputs}
        />
        <Typography className={classes.tip}>
          *Puedes editar el monto recibido y se actualizará en el resumen de la
          orden
          <br />
          *El nuevo monto recibido debe ser igual o menor al monto total de la
        </Typography>
        <CustomButton
          className={classes.button}
          variant="contained"
          onClick={handleSubmit(handleAmountReceivedSubmit)}
          disabled={disableInputs}
        >
          {disableInputs ? <CircularProgress size={30} /> : 'Confirmar'}
        </CustomButton>
        <CustomButton
          className={classes.button}
          onClick={handleCloseModal}
          disabled={disableInputs}
        >
          Cancelar
        </CustomButton>
      </div>
    </CustomModal>
  );
}

PendingModal.propTypes = {
  isSaving: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateOrderStateWithErrorCheck: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  isFetchingOrderStockErrors: PropTypes.bool,
};
